import * as React from 'react';

import { useRouter } from 'next/router';

import {
  mutationAdminCreateOneTalentProfile,
  mutationAdminUpdateTalentProfile,
  queryAdminTalent,
} from '@itp/prisma-talent/gql';
import { QueryAdminTalentProfile, useMutation, usePrivateApollo, useUser } from '@itp/talent';

type AdminProfile = QueryAdminTalentProfile;
type UserProfile = ReturnType<typeof useUser>['user']['profile'];
type ProfileData = AdminProfile | null | UserProfile;

interface ProfileContextValue {
  isAdminMode: boolean;
  isCreateMode: boolean;
  loading: boolean;
  profile: ProfileData;
  refetch: () => Promise<void>;
  submitProfile: (values: Record<string, unknown>) => Promise<any>;
}

const ProfileContext = React.createContext<null | ProfileContextValue>(null);

export const useProfileContext = () => {
  const ctx = React.useContext(ProfileContext);
  if (!ctx) {
    throw new Error('useProfileContext must be used within ProfileProvider');
  }
  return ctx;
};

interface ProfileProviderProps {
  children: React.ReactNode;
}

export const ProfileProvider: React.FC<ProfileProviderProps> = ({ children }) => {
  const router = useRouter();
  const { refetch: refetchUser, user } = useUser();

  const queryId = router.query.id;
  const hasIdParam = queryId !== undefined;
  const profileIdFromQuery = typeof queryId === 'string' ? queryId : '';
  const isAdminMode = hasIdParam && !!user.isAdmin;

  const [createdProfileId, setCreatedProfileId] = React.useState<null | string>(null);

  const effectiveProfileId = createdProfileId || profileIdFromQuery;
  const isCreateMode = isAdminMode && !effectiveProfileId;

  const {
    data: adminData,
    loading: adminLoading,
    refetch: adminRefetch,
  } = usePrivateApollo<QueryAdminTalentProfile>({
    fetchPolicy: 'network-only',
    query: queryAdminTalent,
    skip: !isAdminMode || !effectiveProfileId,
    variables: {
      distinct: ['companyId'],
      where: { id: effectiveProfileId },
    },
  });

  const [createMutation] = useMutation(mutationAdminCreateOneTalentProfile, {
    messageSuccess: 'Profile created successfully',
  });

  const [updateMutation] = useMutation(mutationAdminUpdateTalentProfile, {
    messageSuccess: 'Profile updated successfully',
  });

  const profile = React.useMemo((): ProfileData => {
    if (isAdminMode) {
      return isCreateMode ? null : (adminData ?? null);
    }
    return user?.profile ?? null;
  }, [isAdminMode, isCreateMode, adminData, user?.profile]);

  const loading = isAdminMode ? adminLoading : !!user?.loading;

  const refetch = React.useCallback(async () => {
    if (isAdminMode) {
      await adminRefetch?.();
    } else {
      await refetchUser?.();
    }
  }, [isAdminMode, adminRefetch, refetchUser]);

  const submitProfile = React.useCallback(
    async (values: Record<string, unknown>): Promise<{ id: string } | null> => {
      if (isAdminMode) {
        if (!effectiveProfileId) {
          const result = (await createMutation({ variables: { data: values } })) as
            | { adminCreateOneTalentProfile?: { id?: string } }
            | undefined;
          const newId = result?.adminCreateOneTalentProfile?.id;
          if (newId) {
            setCreatedProfileId(newId);
            router.replace({ pathname: router.pathname, query: { id: newId } }, undefined, {
              shallow: true,
            });
            return { id: newId };
          }
          return null;
        }
        await updateMutation({ variables: { data: values, where: { id: effectiveProfileId } } });
        await adminRefetch?.();
        return { id: effectiveProfileId };
      }
      return null;
    },
    [isAdminMode, effectiveProfileId, createMutation, updateMutation, adminRefetch, router],
  );

  const value = React.useMemo(
    (): ProfileContextValue => ({
      isAdminMode,
      isCreateMode,
      loading,
      profile,
      refetch,
      submitProfile,
    }),
    [isAdminMode, isCreateMode, loading, profile, refetch, submitProfile],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};
